﻿import { Plus, ChevronRight, RefreshCw, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import CustomQuickButton from "@/components/desktop/list/quickButtons/CustomQuickButton";
import CustomButton from "@/components/desktop/button/CustomButton";
import { useWindowSize } from "@/hooks/SizeHook";

import styles from "./custom-title.module.scss";

const CustomTitle = ({
	title,
	subtitle,
	quickActions = [],
	showCreate = false,
	createLabel = "Crear",
	onCreate = () => {},
	leftExtra = null,
	rightExtra = null,
	isFetching = false,
	showBackButton = true,
}) => {
	const navigate = useNavigate();
	const { width: viewportWidth } = useWindowSize();
	const isMobileViewport = typeof viewportWidth === "number" && viewportWidth < 768;

	const isBreadcrumb = Array.isArray(title);
	const showQuick = Array.isArray(quickActions) && quickActions.length > 0;

	let titleContent;
	if (showQuick && !isMobileViewport) {
		titleContent = <CustomQuickButton quickActions={quickActions} subtitle={subtitle} />;
	} else if (showQuick && isMobileViewport) {
		const activeAction = quickActions.find((action) => action.isActive);
		const mobileCrumbs = [{ label: "Dashboard", onClick: () => navigate("/") }];
		if (activeAction?.path) {
			const segments = activeAction.path.split("/").filter(Boolean);
			let accumulatedPath = "";
			for (let i = 0; i < segments.length - 1; i++) {
				accumulatedPath += "/" + segments[i];
				const segmentPath = accumulatedPath;
				const label = segments[i].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
				mobileCrumbs.push({ label, onClick: () => navigate(segmentPath) });
			}
		}
		if (activeAction) {
			mobileCrumbs.push({ label: activeAction.label });
		}
		titleContent = (
			<nav aria-label="Breadcrumb" className={styles.breadcrumb}>
				<ol>
					{mobileCrumbs.map((crumb, idx) => {
						const isLast = idx === mobileCrumbs.length - 1;
						return (
							<li key={`${crumb.label}-${idx}`} className={styles.crumb}>
								<button
									type="button"
									onClick={crumb.onClick}
									className={`${styles.crumbBtn} ${isLast ? styles.crumbCurrent : ""}`}
									disabled={!crumb.onClick}
								>
									{crumb.label}
								</button>
								{!isLast && <ChevronRight className={styles.sep} strokeWidth={2} aria-hidden="true" />}
							</li>
						);
					})}
				</ol>
			</nav>
		);
	} else if (!isBreadcrumb) {
		titleContent = (
			<>
				<h1 className={styles.title}>{title}</h1>
				{subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
			</>
		);
	} else {
		titleContent = (
			<>
				<nav aria-label="Breadcrumb" className={styles.breadcrumb}>
					<ol>
						{title.map((crumb, idx) => {
							const isLast = idx === title.length - 1;
							const content = crumb.href ? (
								<a href={crumb.href} onClick={crumb.onClick} className={`${styles.crumbLink} ${isLast ? styles.crumbCurrent : ""}`}>
									{crumb.label}
								</a>
							) : (
								<button
									type="button"
									onClick={crumb.onClick}
									className={`${styles.crumbBtn} ${isLast ? styles.crumbCurrent : ""}`}
									disabled={!crumb.onClick}
								>
									{crumb.label}
								</button>
							);

							return (
								<li key={`${crumb.label}-${idx}`} className={styles.crumb}>
									{content}
									{!isLast && <ChevronRight className={styles.sep} strokeWidth={2} aria-hidden="true" />}
								</li>
							);
						})}
					</ol>
				</nav>
				{subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
			</>
		);
	}

	return (
		<header className={styles.header}>
			<div className={styles.left}>
				{showBackButton && (
					<button className={styles.backButton} onClick={() => navigate(-1)} type="button" title="Volver">
						<ArrowLeft size={18} strokeWidth={2} />
					</button>
				)}

				<div className={styles.titleWrapper}>
					{leftExtra ? <div className={styles.leftExtra}>{leftExtra}</div> : null}
					{titleContent}
				</div>
			</div>

			<div className={styles.actions}>
				{rightExtra ? <div className={styles.rightExtra}>{rightExtra}</div> : null}

				{isFetching && (
					<div className={styles.fetchingIndicator}>
						<RefreshCw className={styles.fetchingIcon} strokeWidth={2} size={16} />
						<span className={styles.fetchingText}>Actualizando...</span>
					</div>
				)}

				{showCreate && (
					<CustomButton variant="primary" size="sm" startIcon={<Plus strokeWidth={2} size={18} />} onClick={onCreate}>
						<span className={styles.btnLabel}>{createLabel}</span>
					</CustomButton>
				)}
			</div>
		</header>
	);
};

export default CustomTitle;
